"use client";

import {
  GetCheckoutForCartDocument,
  CheckoutFindDocument,
  CheckoutLinesDocument,
  CheckoutLanguageCodeUpdateDocument,
  GetCheckoutPairEyewearValidationDocument,
  CheckoutPricingPageEnum,
  CheckoutWithProximityDocument,
} from "@/api/saleor/generated/graphql";
import type { GetCheckoutPairEyewearValidationQuery } from "@/api/saleor/generated/graphql";
import { isTransactionalClient } from "@/apiUtils/commonClientUtils";
import { getCartCount, getCartIdByLocale } from "@/apiUtils/commonUtils";
import { localeConstants } from "@/apiUtils/constants";
import { getFromCookieMetadataClient } from "@/apiUtils/cookieDataUtils";
import { Checkout, CheckoutLines, CheckoutResponse } from "@/common/lib/types";
import { getClientCookie } from "@/utils/common";
import PublicSaleorClient from "./PublicSaleorClient";

/**
 * Client-side function to get cart details directly from Saleor
 */
export async function getCartClient(
  cartIdIn?: string,
  page: CheckoutPricingPageEnum = CheckoutPricingPageEnum.Cart,
): Promise<CheckoutResponse | null> {
  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    let cartId = cartIdIn;

    if (!cartId) {
      try {
        const isTransactional = isTransactionalClient(locale);

        if (isTransactional) {
          const cartIdByLocale = getCartIdByLocale(locale);
          if (!cartIdByLocale) return null;
          cartId = getFromCookieMetadataClient(cartIdByLocale) || undefined;
        } else {
          return null;
        }
      } catch (error) {
        return null;
      }
    }

    if (!cartId) {
      return null;
    }

    await saleorClient.init(locale);

    const useCartQuery = page === CheckoutPricingPageEnum.Cart;
    const response: { data: CheckoutResponse; errors: unknown } = await saleorClient.post(
      `${useCartQuery ? GetCheckoutForCartDocument : CheckoutFindDocument}`,
      useCartQuery ? { id: cartId } : { id: cartId, page },
      "no-store",
    );

    if (response?.errors) {
      console.error("getCartClient partial errors:", response.errors);
    }

    return response?.data || null;
  } catch (error) {
    return null;
  }
}

/**
 * Client-side function to get only `promotionProximity` for a checkout
 */
export async function getCheckoutProximityClient(
  cartId: string,
  page: CheckoutPricingPageEnum = CheckoutPricingPageEnum.Cart,
): Promise<Checkout | null> {
  if (!cartId) {
    return null;
  }

  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    await saleorClient.init(locale);

    const response: { data: { checkout: Checkout }; errors: unknown } = await saleorClient.post(
      `${CheckoutWithProximityDocument}`,
      { id: cartId, page },
      "no-store",
    );

    if (response?.errors) {
      console.error("getCheckoutProximityClient errors:", response.errors);
      return null;
    }

    return response?.data?.checkout || null;
  } catch (error) {
    return null;
  }
}

/**
 * Client-side function to get cart line items count directly from Saleor
 */
export async function getLineItemsCountClient(): Promise<CheckoutResponse | null> {
  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    try {
      const isTransactional = isTransactionalClient(locale);

      if (isTransactional) {
        const cartIdByLocale = getCartIdByLocale(locale);
        if (!cartIdByLocale) return null;
        const cartId = getFromCookieMetadataClient(cartIdByLocale);
        if (!cartId) return null;

        await saleorClient.init(locale);

        const response: { data: CheckoutResponse; errors: unknown } = await saleorClient.post(
          `${CheckoutLinesDocument}`,
          { id: cartId },
          "no-store",
        );

        if (response?.errors) {
          return null;
        }

        return response?.data || null;
      }
    } catch (error) {
      return null;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Client-side function to fetch cart items and calculate count
 * @returns {Promise<{count: number, lines: CheckoutLines[]}>} The count of items in the cart and the lines in the cart
 */
export async function fetchCartItemsClient(): Promise<{count: number, lines: CheckoutLines[]}> {
  try {
    const lineItemsCount: CheckoutResponse | null = (await getLineItemsCountClient()) || null;
    const count = getCartCount(lineItemsCount?.checkout?.lines ?? []) || 0;
    return {count, lines: lineItemsCount?.checkout?.lines ?? []};
  } catch (error) {
    console.error("Error fetching cart items:", error);
    return {count: 0, lines: []};
  }
}

/**
 * Client-side function to update checkout language
 * @param id Checkout ID
 * @param languageCode Language code to update
 * @returns Promise<CheckoutResponse | null>
 */
export async function updateCheckoutLanguageClient(
  id: string,
  languageCode: string,
): Promise<CheckoutResponse | null> {
  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    if (!id) {
      return null;
    }

    await saleorClient.init(locale);

    const response: { data: { checkoutLanguageCodeUpdate: CheckoutResponse }; errors: unknown } = await saleorClient.post(
      `${CheckoutLanguageCodeUpdateDocument}`,
      { id, languageCode },
      "no-store",
    );

    if (response?.errors) {
      return null;
    }

    return response?.data?.checkoutLanguageCodeUpdate || null;
  } catch (error) {
    console.error("Error updating checkout language:", error);
    return null;
  }
}

/**
 * Client-side Pair Eyewear validation: calls Saleor GraphQL.
 */
export async function validateCheckoutPairEyewearClient(
  checkoutId: string,
): Promise<GetCheckoutPairEyewearValidationQuery["checkoutPairEyewearValidation"] | null> {
  try {
    if (!checkoutId) {
      return null;
    }

    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    await saleorClient.init(locale);

    const response: {
      data: GetCheckoutPairEyewearValidationQuery;
      errors: unknown;
    } = await saleorClient.post(
      `${GetCheckoutPairEyewearValidationDocument}`,
      { id: checkoutId },
      "no-store",
    );

    if (response?.errors) {
      try {
        console.error(
          "\n SALEOR (client): validate_checkout_pair_eyewear_failed:",
          JSON.stringify(response?.errors, null, 2),
        );
      } catch {
        console.error("\n SALEOR (client): validate_checkout_pair_eyewear_failed:", response?.errors);
      }
      return null;
    }

    return response?.data?.checkoutPairEyewearValidation ?? null;
  } catch (error) {
    console.error("validateCheckoutPairEyewearClient:", error);
    return null;
  }
}
